export type AccountType = "individual" | "business";

export type Role =
  | "owner"
  | "admin"
  | "operator"
  | "shipment_editor"
  | "viewer"
  | "viewer_no_billing"
  | "guest"
  | "driver"
  | "ops_manager"
  | "client_care"
  | "platform_admin"
  | "super_admin";

export interface Account {
  id: string;
  /** Short public code, e.g. HEM001. This is what the courier sees. */
  code: string;
  default_collection_address_id?: string;
  type: AccountType;
  legal_name: string;
  trading_name?: string;
  billing_email: string;
  phone?: string;
  status: "active" | "suspended" | "closed";
  billing_mode: "prepaid" | "invoiced";
  balance_cents?: number;
  created_at: string;
}

export interface User {
  id: string;
  account_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  is_active: boolean;
  last_login_at?: string;
  created_at?: string;
}

export interface Tokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

export interface Session {
  account: Account;
  user: User;
  tokens: Tokens;
}

export interface Address {
  id?: string;
  /** ShipLogic routes and prices differently by address type. */
  type?: "business" | "residential" | "counter" | "locker";
  label?: string;
  company_name?: string;
  contact_name: string;
  phone: string;
  email?: string;
  street: string;
  complex?: string;
  suburb: string;
  city: string;
  province: string;
  postal_code: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  instructions?: string;
  is_default?: boolean;
}

export interface Parcel {
  description?: string;
  length_mm: number;
  width_mm: number;
  height_mm: number;
  weight_g: number;
}

/**
 * One row of what a customer is paying for: the shipping itself, then each
 * extra they chose or triggered. These always sum to price_cents.
 *
 * Every amount is a difference between two prices, never a share of our cost
 * -- see pricing.Breakdown on the server. Showing them is safe; computing
 * anything from them is not, so the panel adds them up and nothing else.
 */
export interface PriceLine {
  label: string;
  amount_cents: number;
}

export interface RateOption {
  id: string;
  courier_code: string;
  courier_name: string;
  service_level: string;
  service_name: string;
  service_note?: string;
  price_cents: number;
  vat_cents: number;
  currency: string;
  transit_days: number;
  collection_cutoff?: string;
  estimated_delivery_date?: string;
  /** What this option can actually honour -- see domain.Schedule. Dates are
   *  YYYY-MM-DD, windows are "HH:MM"; absent means the level sets no bound. */
  earliest_collection_date?: string;
  expected_delivery_from?: string;
  expected_delivery_to?: string;
  collection_window_start?: string;
  collection_window_end?: string;
  delivery_window_start?: string;
  delivery_window_end?: string;
  badge?: "cheapest" | "fastest" | "recommended";
  /** What this price is made of. Absent on older quotes; never empty. */
  lines?: PriceLine[];
}

export interface Quote {
  id: string;
  account_id: string;
  options: RateOption[];
  expires_at: string;
  created_at: string;
}

export type ShipmentStatus =
  | "draft"
  | "booked"
  | "awaiting_collection"
  | "collected"
  | "in_transit"
  | "at_hub"
  | "out_for_delivery"
  | "delivered"
  | "attempt_failed"
  | "collection_failed"
  | "exception"
  | "cancelled"
  | "returned";

export interface TrackingEvent {
  status: ShipmentStatus;
  label?: string;
  code?: string;
  description: string;
  location?: string;
  occurred_at: string;
  source?: string;
}

export interface Shipment {
  id: string;
  account_id: string;
  waybill: string;
  reference?: string;
  status: ShipmentStatus;
  service_level: string;
  courier_code: string;
  courier_name: string;
  courier_reference?: string;
  collection: Address;
  delivery: Address;
  parcels: Parcel[];
  declared_value_cents: number;
  insurance_cents: number;
  price_cents: number;
  vat_cents: number;
  collection_date?: string;
  collected_at?: string;
  estimated_delivery_at?: string;
  delivered_at?: string;
  requested_collection_after?: string;
  requested_collection_before?: string;
  requested_delivery_after?: string;
  requested_delivery_before?: string;
  label_url?: string;
  special_instructions?: string;
  test_mode: boolean;
  created_at: string;
  events?: TrackingEvent[];
}

export interface DashboardStats {
  active: number;
  in_transit: number;
  delivered_this_month: number;
  exceptions: number;
  spend_this_month_cents: number;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  mode: "live" | "test";
  last_used_at?: string;
  revoked_at?: string;
  created_at: string;
}

export interface PublicTracking {
  waybill: string;
  status: ShipmentStatus;
  status_label: string;
  courier_name: string;
  service_level: string;
  destination_city: string;
  destination_suburb: string;
  parcel_count: number;
  estimated_delivery_at?: string;
  delivered_at?: string;
  events: TrackingEvent[];
}

export interface Paged<T> {
  data: T[];
  meta: { total: number; limit: number; offset: number };
}

// ---------- platform administration ----------

export interface PlatformStats {
  accounts: number;
  active_accounts: number;
  new_accounts_30d: number;
  users: number;
  shipments_today: number;
  shipments_30d: number;
  in_transit: number;
  exceptions: number;
  revenue_30d_cents: number;
  cost_30d_cents: number;
  margin_30d_cents: number;
}

export interface DailyVolume {
  day: string;
  shipments: number;
  revenue_cents: number;
  margin_cents: number;
}

export interface ImpersonationSession {
  id: string;
  actor_email: string;
  subject_email: string;
  subject_account_id: string;
  reason: string;
  started_at: string;
  ended_at?: string;
  expires_at: string;
}

export interface PlatformDashboard {
  stats: PlatformStats;
  volume: DailyVolume[];
  recent_impersonations?: ImpersonationSession[];
}

export interface AccountSummary extends Account {
  users: number;
  shipments: number;
  spend_30d_cents: number;
  last_activity_at?: string;
  owner_email: string;
}

export interface PlatformUser extends User {
  account_name: string;
  is_staff: boolean;
}

export interface RoleDefinition {
  role: string;
  label: string;
  is_staff: boolean;
  assignable: boolean;
  permissions: string[];
}

// ---------- wallet ----------

export type LedgerKind =
  | "topup"
  | "topup_reversal"
  | "shipment_charge"
  | "shipment_refund"
  | "admin_credit"
  | "admin_debit"
  | "promotional_credit"
  | "adjustment_credit"
  | "adjustment_debit"
  | "refund"
  | "refund_reversal";

export interface LedgerEntry {
  id: string;
  account_id: string;
  kind: LedgerKind;
  kind_label: string;
  direction: "credit" | "debit";
  amount_cents: number;
  balance_after_cents: number;
  description?: string;
  reference?: string;
  shipment_id?: string;
  payment_id?: string;
  source: "system" | "customer" | "admin" | "gateway";
  reverses_id?: string;
  reversed_by_id?: string;
  created_at: string;
}

export interface WalletSummary {
  balance_cents: number;
  credit_limit_cents: number;
  available_cents: number;
  currency: string;
  billing_mode: "prepaid" | "invoiced";
  low_balance: boolean;
  spend_mtd_cents: number;
  topped_up_mtd_cents: number;
}

export interface Payment {
  id: string;
  amount_cents: number;
  status: "pending" | "succeeded" | "failed" | "cancelled" | "reversed";
  provider: string;
  method?: string;
  redirect_url?: string;
  failure_reason?: string;
  created_at: string;
  settled_at?: string;
}

export interface TransactionKind {
  kind: LedgerKind;
  label: string;
  direction: "credit" | "debit";
}

export interface AdminWallet {
  account: Account;
  wallet: WalletSummary;
  payments: Payment[];
  entries: LedgerEntry[];
  meta: { total: number; limit: number; offset: number };
}

export type WebhookEvent =
  | "shipment.created"
  | "shipment.tracking_updated"
  | "address.created"
  | "address.archived";

export const ALL_WEBHOOK_EVENTS: WebhookEvent[] = [
  "shipment.created",
  "shipment.tracking_updated",
  "address.created",
  "address.archived",
];

export interface WebhookEndpoint {
  id: string;
  account_id: string;
  url: string;
  events: WebhookEvent[];
  is_active: boolean;
  created_at: string;
}

export interface CarrierWebhookIntegration {
  id: string;
  label: string;
  is_active: boolean;
  created_at: string;
}

export interface CarrierWebhookEvent {
  id: string;
  integration_id?: string;
  raw_body: unknown;
  shipment_id?: string;
  status?: string;
  parsed: boolean;
  error?: string;
  received_at: string;
}

export interface PlatformWebhookDestination {
  id: string;
  label: string;
  url: string;
  is_active: boolean;
  created_at: string;
}

/**
 * One address suggestion from the server-side lookup proxy
 * (GET /v1/geocode/search). The vendor key never reaches the browser: a maps
 * key in a bundle is a key anyone can read out of the bundle.
 *
 * A result is a suggestion, not a fact. Picking one fills the location fields
 * and carries a coordinate, which is what lets pricing measure a real route
 * rather than fall back to geocoding typed text server-side.
 */
export interface GeocodeResult {
  label: string;
  street: string;
  suburb: string;
  city: string;
  province: string;
  postal_code: string;
  country: string;
  latitude: number;
  longitude: number;
}

/**
 * What POST /v1/shipments/checkout answers.
 *
 * A booking has two endings and the caller cannot know in advance which one it
 * will get, because it depends on the wallet balance at the moment the price
 * is charged. `paid_from: "wallet"` means it is booked and `shipment` is the
 * booking. `paid_from: "card"` means nothing is booked yet: send the customer
 * to `redirect_url`, and the shipment appears once the gateway's webhook
 * settles the payment.
 *
 * `price_cents` and `wallet_used_cents` are what the customer is paying and
 * what their own credit covered -- the only two figures a customer ever needs
 * to see. How the price was arrived at is ours.
 */
export interface CheckoutResult {
  paid_from: "wallet" | "card";
  shipment?: Shipment;
  payment_id?: string;
  redirect_url?: string;
  amount_cents?: number;
  wallet_used_cents?: number;
  price_cents?: number;
  notice?: string;
}

/** What GET /v1/shipments/checkout/{payment_id} answers while we wait. */
export interface CheckoutStatus {
  status: "pending" | "booked" | "failed";
  charged_cents: number;
  shipment_id?: string;
  shipment?: Shipment;
  failure_reason?: string;
}
